import dayjs from 'dayjs'

import type { ValidatedAgreementFormData } from '~/app/settings/agreements/components/schema'
import {
  AGREEMENT_CATEGORIES_V1,
  type AgreementFormData,
  type AgreementProductFormData,
  BILLING_CYCLES_V1,
  DEFAULT_COURTESY_PERCENT,
  DEFAULT_COURTESY_WEEK_START_DAY,
  DELINQUENCY_TEST_TYPES,
  DISCOUNT_APPLICATIONS,
  DISCOUNT_PERIODS,
  ENTITY_COUNT_LEVEL_AUTOMATIC,
  TIER_QUANTITY_SOURCE_PRICE_DEFAULT
} from '~/app/settings/agreements/types'
import { parseAgreementFormData } from '~/app/settings/agreements/utils'
import type { AgreementCreateParams } from '~/data/api/client/billing'
import {
  AGREEMENT_LANGUAGES,
  AGREEMENT_TIER_QUANTITY_SOURCES,
  COLLECTION_POLICIES,
  DEMAND_CHARGE_ALLOCATION_METHODS,
  ENTITY_COUNT_LEVELS,
  LINE_ITEM_GROUPINGS,
  PEAK_SAVER_SAVE_TYPES
} from '~/data/types/billing'
import { BILLING_CURRENCIES } from '~/data/types/currencies'
import { parseFromSmallestDenomination } from '~/data/utils/currency'

import type { AgreementDraftFields, DraftApplyResult, SkippedDraftField } from '../types'
import { isFiniteNumber, isOneOf, isRecord, isStringArray, skipped } from './shared'

export type AgreementDraftToFormOptions = {
  /** Editing an existing agreement: partner organization and settlement currency are frozen. */
  isEditing: boolean
}

const AGREEMENT_TYPES = ['MARKETPLACE', 'PRIVATE', 'PUBLIC'] as const satisfies readonly AgreementCreateParams['type'][]
const DAYS_OF_WEEK = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'] as const
const AUTHORIZED_ENTITY_TYPES = ['FLEET', 'VEHICLE', 'USER', 'USER_GROUP', 'AUTH_TAG'] as const

type AgreementDraft = Partial<AgreementCreateParams>

/**
 * Maps a (possibly partial) agreement draft — the billing-api request shape, money in cents — onto the
 * wizard's form state. Never throws: unknown keys, unsupported values and immutable-on-edit fields are
 * reported in `skipped`; everything else lands in `next`.
 */
export function agreementDraftToForm(
  fields: AgreementDraftFields,
  current: AgreementFormData,
  options: AgreementDraftToFormOptions
): DraftApplyResult<AgreementFormData> {
  const applied: string[] = []
  const skippedFields: SkippedDraftField[] = []
  const next: AgreementFormData = {
    ...current,
    products: [...current.products],
    discounts: [...current.discounts],
    commitments: [...current.commitments],
    metadataEntries: [...current.metadataEntries]
  }

  const apply = <K extends keyof AgreementFormData>(key: K, path: string, value: AgreementFormData[K]) => {
    next[key] = value
    applied.push(path)
  }
  const string = (key: string, value: unknown, assign: (value: string) => void) => {
    if (typeof value === 'string') {
      assign(value)
      applied.push(key)
    } else {
      skippedFields.push(skipped(key, 'unsupportedValue'))
    }
  }
  const number = (key: string, value: unknown, assign: (value: number) => void) => {
    if (isFiniteNumber(value)) {
      assign(value)
      applied.push(key)
    } else {
      skippedFields.push(skipped(key, 'unsupportedValue'))
    }
  }
  const boolean = (key: string, value: unknown, assign: (value: boolean) => void) => {
    if (typeof value === 'boolean') {
      assign(value)
      applied.push(key)
    } else {
      skippedFields.push(skipped(key, 'unsupportedValue'))
    }
  }
  const enumeration = <const T extends readonly string[]>(
    key: string,
    value: unknown,
    allowed: T,
    assign: (value: T[number]) => void
  ) => {
    if (isOneOf(value, allowed)) {
      assign(value)
      applied.push(key)
    } else {
      skippedFields.push(skipped(key, 'unsupportedValue'))
    }
  }

  const raw = fields as Record<string, unknown>
  for (const key of Object.keys(raw)) {
    const value = raw[key]
    if (value === undefined) continue

    switch (key) {
      case 'name':
        string(key, value, (name) => apply('name', key, name))
        break
      case 'partnerOrganizationId':
        if (typeof value !== 'string') {
          skippedFields.push(skipped(key, 'unsupportedValue'))
        } else if (value === current.partnerOrganizationId) {
          break
        } else if (options.isEditing) {
          skippedFields.push(skipped(key, 'immutableOnEdit'))
        } else {
          apply('partnerOrganizationId', key, value)
        }
        break
      case 'settlementCurrency':
        if (!isOneOf(value, BILLING_CURRENCIES)) {
          skippedFields.push(skipped(key, 'unsupportedValue'))
        } else if (value === current.settlementCurrency) {
          break
        } else if (options.isEditing) {
          skippedFields.push(skipped(key, 'immutableOnEdit'))
        } else {
          apply('settlementCurrency', key, value)
        }
        break
      case 'type':
        enumeration(key, value, AGREEMENT_TYPES, (type) => apply('type', key, type))
        break
      case 'category':
        enumeration(key, value, AGREEMENT_CATEGORIES_V1, (category) => apply('category', key, category))
        break
      case 'billingCycle':
        enumeration(key, value, BILLING_CYCLES_V1, (cycle) => apply('billingCycle', key, cycle))
        break
      case 'collectionPolicy':
        enumeration(key, value, COLLECTION_POLICIES, (policy) => apply('collectionPolicy', key, policy))
        break
      case 'lineItemGrouping':
        enumeration(key, value, LINE_ITEM_GROUPINGS, (grouping) => apply('lineItemGrouping', key, grouping))
        break
      case 'startDate':
        string(key, value, (date) => apply('startDate', key, date))
        break
      case 'endDate':
        if (value === null) {
          apply('endDate', key, null)
        } else {
          string(key, value, (date) => apply('endDate', key, date))
        }
        break
      case 'autoCharge':
        boolean(key, value, (flag) => apply('autoCharge', key, flag))
        break
      case 'invoiceDueDays':
        number(key, value, (days) => apply('invoiceDueDays', key, days))
        break
      case 'billingAnchorDayOfMonth':
        number(key, value, (day) => apply('billingAnchorDayOfMonth', key, day))
        break
      case 'billingAnchorDayOfWeek':
        enumeration(key, value, DAYS_OF_WEEK, (day) => apply('billingAnchorDayOfWeek', key, day))
        break
      case 'authorizationChargeEnabled':
        boolean(key, value, (flag) => apply('authorizationChargeEnabled', key, flag))
        break
      case 'authorizationCharge':
        number(key, value, (cents) => {
          apply('authorizationCharge', key, parseFromSmallestDenomination(cents))
          next.authorizationChargeEnabled = true
        })
        break
      case 'peakSaverEnabled':
        boolean(key, value, (flag) => apply('peakSaverEnabled', key, flag))
        break
      case 'peakSaverConfiguration':
        applyPeakSaver(value, next, applied, skippedFields)
        break
      case 'demandChargeAllocationConfiguration':
        applyDemandCharge(value, next, applied, skippedFields)
        break
      case 'delinquencyEnforcementEnabled':
        boolean(key, value, (flag) => apply('delinquencyEnforcementEnabled', key, flag))
        break
      case 'delinquencyTestType':
        enumeration(key, value, DELINQUENCY_TEST_TYPES, (testType) => apply('delinquencyTestType', key, testType))
        break
      case 'delinquencyThreshold': {
        const testType = isOneOf(raw.delinquencyTestType, DELINQUENCY_TEST_TYPES)
          ? raw.delinquencyTestType
          : current.delinquencyTestType
        number(key, value, (threshold) =>
          apply(
            'delinquencyThreshold',
            key,
            testType === 'OUTSTANDING_BALANCE' ? parseFromSmallestDenomination(threshold) : threshold
          )
        )
        break
      }
      case 'prepaymentEnabled':
        boolean(key, value, (flag) => apply('prepaymentEnabled', key, flag))
        break
      case 'initialPrepaymentAmount':
        number(key, value, (cents) => apply('initialPrepaymentAmount', key, parseFromSmallestDenomination(cents)))
        break
      case 'topOffThreshold':
        number(key, value, (cents) => apply('topOffThreshold', key, parseFromSmallestDenomination(cents)))
        break
      case 'primaryContactName':
        string(key, value, (name) => apply('primaryContactName', key, name))
        break
      case 'primaryContactEmail':
        string(key, value, (email) => apply('primaryContactEmail', key, email))
        break
      case 'invoiceTemplateId':
        string(key, value, (id) => apply('invoiceTemplateId', key, id))
        break
      case 'language':
        enumeration(key, value, AGREEMENT_LANGUAGES, (language) => apply('language', key, language))
        break
      case 'metadata':
        if (isRecord(value)) {
          const entries = Object.entries(value)
            .filter((entry): entry is [string, string] => typeof entry[1] === 'string')
            .map(([metadataKey, metadataValue]) => ({ key: metadataKey, value: metadataValue }))
          apply('metadataEntries', key, entries.length > 0 ? entries : [{ key: '', value: '' }])
        } else {
          skippedFields.push(skipped(key, 'unsupportedValue'))
        }
        break
      case 'products':
        applyProducts(value, next, applied, skippedFields)
        break
      case 'discounts':
        applyDiscounts(value, next, applied, skippedFields)
        break
      case 'commitments':
        applyCommitments(value, next, applied, skippedFields)
        break
      case 'organizationId':
      case 'autoRenew':
      case 'noticePeriodDays':
      case 'terms':
      case 'sourceOfferingId':
      case 'authorizeAllPayorEntities':
        skippedFields.push(skipped(key, 'notInForm'))
        break
      default:
        skippedFields.push(skipped(key, 'unknownField'))
    }
  }

  return { next, applied, skipped: skippedFields }
}

function applyPeakSaver(
  value: unknown,
  next: AgreementFormData,
  applied: string[],
  skippedFields: SkippedDraftField[]
): void {
  if (!isRecord(value)) {
    skippedFields.push(skipped('peakSaverConfiguration', 'unsupportedValue'))
    return
  }
  if (typeof value.enabled === 'boolean') {
    next.peakSaverEnabled = value.enabled
    applied.push('peakSaverConfiguration.enabled')
  }
  if (value.saveType !== undefined) {
    if (isOneOf(value.saveType, PEAK_SAVER_SAVE_TYPES)) {
      next.peakSaverSaveType = value.saveType
      applied.push('peakSaverConfiguration.saveType')
    } else {
      skippedFields.push(skipped('peakSaverConfiguration.saveType', 'unsupportedValue'))
    }
  }
  if (value.quantity !== undefined) {
    if (isFiniteNumber(value.quantity)) {
      next.peakSaverQuantity = value.quantity
      applied.push('peakSaverConfiguration.quantity')
    } else {
      skippedFields.push(skipped('peakSaverConfiguration.quantity', 'unsupportedValue'))
    }
  }
}

function applyDemandCharge(
  value: unknown,
  next: AgreementFormData,
  applied: string[],
  skippedFields: SkippedDraftField[]
): void {
  if (!isRecord(value)) {
    skippedFields.push(skipped('demandChargeAllocationConfiguration', 'unsupportedValue'))
    return
  }
  if (typeof value.enabled === 'boolean') {
    next.demandChargeAllocationEnabled = value.enabled
    applied.push('demandChargeAllocationConfiguration.enabled')
  }
  if (value.allocationMethod !== undefined) {
    if (isOneOf(value.allocationMethod, DEMAND_CHARGE_ALLOCATION_METHODS)) {
      next.demandChargeAllocationMethod = value.allocationMethod
      applied.push('demandChargeAllocationConfiguration.allocationMethod')
    } else {
      skippedFields.push(skipped('demandChargeAllocationConfiguration.allocationMethod', 'unsupportedValue'))
    }
  }
  if (value.passThroughPercentage !== undefined) {
    if (isFiniteNumber(value.passThroughPercentage)) {
      next.demandChargePassThroughPercentage = value.passThroughPercentage
      applied.push('demandChargeAllocationConfiguration.passThroughPercentage')
    } else {
      skippedFields.push(skipped('demandChargeAllocationConfiguration.passThroughPercentage', 'unsupportedValue'))
    }
  }
}

/**
 * Draft products become wizard line items. Omitted overrides map to the form's sentinels (`PRICE_DEFAULT`,
 * `AUTOMATIC`) exactly as the edit page does when hydrating a stored agreement. Authorized entities are
 * flattened into the per-type lists the Access step edits (the API repeats them per product).
 */
function applyProducts(
  value: unknown,
  next: AgreementFormData,
  applied: string[],
  skippedFields: SkippedDraftField[]
): void {
  if (!Array.isArray(value)) {
    skippedFields.push(skipped('products', 'unsupportedValue'))
    return
  }
  const lines: AgreementProductFormData[] = []
  const authorized: Record<(typeof AUTHORIZED_ENTITY_TYPES)[number], Set<string>> = {
    FLEET: new Set(),
    VEHICLE: new Set(),
    USER: new Set(),
    USER_GROUP: new Set(),
    AUTH_TAG: new Set()
  }
  let sawAuthorizedEntities = false

  value.forEach((item, index) => {
    if (!isRecord(item) || typeof item.productId !== 'string') {
      skippedFields.push(skipped(`products[${index}]`, 'unsupportedValue'))
      return
    }
    lines.push({
      id: item.productId,
      quantity: isFiniteNumber(item.quantity) ? item.quantity : 1,
      tierQuantitySource: isOneOf(item.tierQuantitySource, AGREEMENT_TIER_QUANTITY_SOURCES)
        ? item.tierQuantitySource
        : TIER_QUANTITY_SOURCE_PRICE_DEFAULT,
      entityCountLevel: isOneOf(item.entityCountLevel, ENTITY_COUNT_LEVELS)
        ? item.entityCountLevel
        : ENTITY_COUNT_LEVEL_AUTOMATIC,
      priceId: typeof item.priceId === 'string' ? item.priceId : '',
      effectiveFrom: typeof item.effectiveFrom === 'string' ? item.effectiveFrom : ''
    })
    if (Array.isArray(item.authorizedEntities)) {
      sawAuthorizedEntities = true
      for (const entity of item.authorizedEntities) {
        if (!isRecord(entity) || typeof entity.entityId !== 'string') continue
        if (isOneOf(entity.entityType, AUTHORIZED_ENTITY_TYPES)) {
          authorized[entity.entityType].add(entity.entityId)
        }
      }
    }
  })

  if (lines.length > 0) {
    next.products = lines
    applied.push('products')
  }
  if (sawAuthorizedEntities) {
    next.authorizedFleets = [...authorized.FLEET]
    next.authorizedVehicles = [...authorized.VEHICLE]
    next.authorizedUsers = [...authorized.USER]
    next.authorizedUserGroups = [...authorized.USER_GROUP]
    next.authorizedAuthTags = [...authorized.AUTH_TAG]
    applied.push('products.authorizedEntities')
  }
}

function applyDiscounts(
  value: unknown,
  next: AgreementFormData,
  applied: string[],
  skippedFields: SkippedDraftField[]
): void {
  if (!Array.isArray(value)) {
    skippedFields.push(skipped('discounts', 'unsupportedValue'))
    return
  }
  const discounts = value.filter(isRecord).map((discount) => {
    const application = isOneOf(discount.application, DISCOUNT_APPLICATIONS) ? discount.application : ('VOLUME' as const)
    const quantityUnit = discount.quantityUnit === 'MINUTES' ? ('MINUTES' as const) : ('KWH' as const)
    const base = {
      name: typeof discount.name === 'string' ? discount.name : '',
      productId: isStringArray(discount.applicableProductIds) ? discount.applicableProductIds[0] : undefined,
      application,
      quantityUnit
    }
    const tiers = Array.isArray(discount.tiers) ? discount.tiers.filter(isRecord) : []
    if (application === 'COURTESY') {
      // A courtesy draft carries one tier per period: its endQuantity is the period's quantity, its amount the percentage.
      const periodQuantity = (period: string) => {
        const tier = tiers.find((candidate) => isOneOf(candidate.period, DISCOUNT_PERIODS) && candidate.period === period)
        return tier && isFiniteNumber(tier.endQuantity) ? tier.endQuantity : undefined
      }
      const percent = tiers.map((tier) => tier.discountAmount).find(isFiniteNumber)
      return {
        ...base,
        tiers: [{ usageAmountStart: 0 }],
        courtesyDailyQuantity: periodQuantity('DAY'),
        courtesyWeeklyQuantity: periodQuantity('WEEK'),
        courtesyPercent: percent ?? DEFAULT_COURTESY_PERCENT,
        courtesyWeekStartDay: isOneOf(discount.weekStartDay, DAYS_OF_WEEK) ? discount.weekStartDay : DEFAULT_COURTESY_WEEK_START_DAY
      }
    }
    return {
      ...base,
      tiers:
        tiers.length > 0
          ? tiers.map((tier) => ({
              discountAmount: isFiniteNumber(tier.discountAmount) ? tier.discountAmount : undefined,
              usageAmountStart: isFiniteNumber(tier.startQuantity) ? tier.startQuantity : 0,
              usageAmountEnd: isFiniteNumber(tier.endQuantity) ? tier.endQuantity : null
            }))
          : [{ usageAmountStart: 0 }]
    }
  })
  next.volumeDiscountEnabled = discounts.length > 0
  if (discounts.length > 0) {
    next.discounts = discounts
  }
  applied.push('discounts')
}

function applyCommitments(
  value: unknown,
  next: AgreementFormData,
  applied: string[],
  skippedFields: SkippedDraftField[]
): void {
  if (!Array.isArray(value)) {
    skippedFields.push(skipped('commitments', 'unsupportedValue'))
    return
  }
  const commitments = value.filter(isRecord).map((commitment) => ({
    name: typeof commitment.name === 'string' ? commitment.name : '',
    productId: isStringArray(commitment.productIds) ? commitment.productIds[0] : undefined,
    usageAmount: isFiniteNumber(commitment.usageQuantity) ? commitment.usageQuantity : undefined,
    // The wizard only edits USAGE commitments; `costAmount` carries the form's rate-above-
    // commitment percentage (0–100), not cents. Matches `parseAgreementToForm` / submit.
    rateAboveCommitment: isFiniteNumber(commitment.costAmount) ? commitment.costAmount : undefined
  }))
  next.usageCommitmentEnabled = commitments.length > 0
  if (commitments.length > 0) {
    next.commitments = commitments
  }
  applied.push('commitments')
}

/**
 * Snapshot of the current form in the draft-fields (API request) shape, for the `billing_draft` context
 * ref. Reuses the wizard's own serializer when the form is complete enough; otherwise falls back to a
 * lenient projection so an unfinished form still gives the assistant what the user sees.
 */
export function agreementFormToDraft(form: AgreementFormData): AgreementDraft {
  try {
    return parseAgreementFormData(form as ValidatedAgreementFormData)
  } catch {
    return lenientAgreementSnapshot(form)
  }
}

function lenientAgreementSnapshot(form: AgreementFormData): AgreementDraft {
  return {
    name: form.name,
    partnerOrganizationId: form.partnerOrganizationId || undefined,
    type: form.type,
    category: form.category,
    billingCycle: form.billingCycle,
    collectionPolicy: form.collectionPolicy,
    lineItemGrouping: form.lineItemGrouping || undefined,
    settlementCurrency: form.settlementCurrency,
    startDate: form.startDate && dayjs(form.startDate).isValid() ? dayjs(form.startDate).toISOString() : undefined,
    endDate: form.endDate && dayjs(form.endDate).isValid() ? dayjs(form.endDate).toISOString() : undefined,
    autoCharge: form.autoCharge,
    invoiceDueDays: form.invoiceDueDays,
    primaryContactName: form.primaryContactName || undefined,
    primaryContactEmail: form.primaryContactEmail || undefined,
    language: form.language,
    prepaymentEnabled: form.prepaymentEnabled,
    peakSaverEnabled: form.peakSaverEnabled,
    products: form.products
      .filter((product) => product.id)
      .map((product) => ({
        productId: product.id,
        quantity: product.quantity,
        priceId: product.priceId || undefined,
        tierQuantitySource:
          product.tierQuantitySource && product.tierQuantitySource !== TIER_QUANTITY_SOURCE_PRICE_DEFAULT
            ? product.tierQuantitySource
            : undefined,
        entityCountLevel:
          product.entityCountLevel && product.entityCountLevel !== ENTITY_COUNT_LEVEL_AUTOMATIC
            ? product.entityCountLevel
            : undefined
      })),
    metadata: Object.fromEntries(
      form.metadataEntries.filter((entry) => entry.key.trim().length > 0).map((entry) => [entry.key, entry.value])
    )
  }
}
